import { $API } from "boot/api";
import {setNonMetrcCategories} from "src/store/data/mutations";
import {clearInventoryReorderCache} from "src/cache/inventoryReorderCache";

let contactsTimer = null;
let contactsInFlight = false;
let contactsAttempts = 0;

function getPreferredDefaultLicense(licenses = []) {
  return (
    licenses.find((license) => license?.is_central_distribution) ||
    licenses[0] ||
    null
  );
}

function isRetryable(err) {
  const s = err?.response?.status;
  const code = err?.code;

  // Don't retry on client errors (400s) except 429
  if (s >= 400 && s < 500 && s !== 429) {
    return false;
  }

  return (
    code === "ECONNABORTED" ||
    code === "ERR_NETWORK" ||
    s === 429 ||
    s === 502 ||
    s === 503 ||
    s === 504
  );
}

export function syncTaxonomies({ dispatch }) {
  //Sync category images taxonomy.
  dispatch("syncCategoryImages"); //TODO uncomment when this route is fixed.
  //Sync category type taxonomy.
  dispatch("syncCategoryTypes");
  //Sync category taxonomy.
  dispatch("syncCategories");
  //Sync subcategory taxonomy.
  dispatch("syncSubcategories");
  //Sync non-metrc-category taxonomy.
  dispatch("syncNonMetrcCategories");
  //Sync trim type taxonomy.
  dispatch("syncTrimTypes");
  //Sync strain type taxonomy.
  dispatch("syncStrainTypes");
  //Sync production type taxonomy.
  dispatch("syncProductionTypes");
  //Sync company brands.
  dispatch("syncBrands");
  //Sync payment terms.
  dispatch("syncPaymentTerms");
  //Sync grow media.
  dispatch("syncGrowMedia");
  //sync filter cateogries.
  dispatch("syncFilterCategories");
  //Sync payment statuses.
  dispatch("syncPaymentStatuses");
}

export function syncCategoryImages({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;
  const retryDelay = 300;

  $API.PUBLIC_IMAGES.GET()
    .then(({ data }) => {
      commit("setCategoryImages", data.category_images);
    })
    .catch((response) => {
      console.log("ERR", response);
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncCategoryImages", retryCount + 1);
        }, retryDelay);
      } else {
        console.log("Max retries reached, giving up.");
      }
    });
}

export function syncCategoryTypes({ commit, dispatch }) {
  $API.CATEGORY_TYPES.GET()
    .then(({ data }) => {
      commit("setCategoryTypes", data.category_types);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncCategoryTypes");
      }, 200);
    });
}

export function syncCategories({ commit, dispatch }, { items = null } = {}) {
  $API.CATEGORIES.GET()
    .then(({ data }) => {
      commit("setCategories", data.categories);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncCategories", { items });
      }, 200);
    });
}

export function syncSubcategories({ commit, dispatch }, { items = null } = {}) {
  $API.SUBCATEGORIES.GET()
    .then(({ data }) => {
      commit("setSubcategories", data.subcategories);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncSubcategories", { items });
      }, 200);
    });
}

export function syncNonMetrcCategories({ commit, dispatch }, { items = null } = {}) {
  $API.NON_METRC_CATEGORIES.GET({
    selectable: true,
    include_groups: true,
  })
    .then(({ data }) => {
      commit("setNonMetrcCategories", data.non_metrc_categories);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncNonMetrcCategories", { items });
      }, 200);
    });
}

export function syncFilterCategories(
  { commit, dispatch },
  { items = null } = {}
) {
  $API.CATEGORIES.GET()
    .then(({ data }) => {
      if (Array.isArray(items) && items.length > 0) {
        const uniqueCategoryTypes = items.reduce((acc, item) => {
          if (
            item.category_type &&
            !acc.find((ct) => ct.id === item.category_type.id)
          ) {
            acc.push(item.category_type);
          }
          if (item.product && item.product.category) {
            acc.push(item.product.category);
          }
          return acc;
        }, []);

        const filteredCategories = data.categories.filter((category) =>
          uniqueCategoryTypes.some((ct) => ct.id === category.id)
        );

        commit("setFilterCategories", filteredCategories);
      } else {
        commit("setFilterCategories", data.categories);
      }
    })
    .catch((error) => {
      console.error("Failed to sync filter categories:", error);
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncFilterCategories", { items });
      }, 200);
    });
}

export function syncTrimTypes({ commit, dispatch }) {
  $API.TRIM_TYPES.GET()
    .then(({ data }) => {
      commit("setTrimTypes", data.trim_types);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncTrimTypes");
      }, 200);
    });
}

export function syncStrainTypes({ commit, dispatch }) {
  $API.STRAIN_TYPES.GET()
    .then(({ data }) => {
      commit("setStrainTypes", data.strain_types);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncStrainTypes");
      }, 200);
    });
}

export function syncProductionTypes({ commit, dispatch }) {
  $API.PRODUCTION_TYPES.GET()
    .then(({ data }) => {
      commit("setProductionTypes", data.production_types);
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncProductionTypes");
      }, 200);
    });
}

export function syncBrands({ commit, dispatch }, brandId = null) {
  $API.BRANDS.GET({
    my: true, //load only current company brands
  })
    .then(({ data }) => {
      commit("setBrands", data.brands);
      if (brandId) {
        dispatch("fetchStorefront", brandId);
      }
    })
    .catch(() => {
      //Sync again on a time out.
      setTimeout(() => {
        dispatch("syncBrands");
      }, 200);
    });
}

export function syncCompany({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3; // Stop after 3 attempts

  $API.CURRENT_COMPANY.GET()
    .then(({ data }) => {
      commit("setCompany", data.company);
    })
    .catch((error) => {
      // Don't retry on 404 - user legitimately has no company
      if (error?.response?.status === 404) {
        console.warn("User has no company associated");
        commit("setCompany", null); // Set to null instead of retrying
        return;
      }

      // Retry on other errors, but with a limit
      if (retryCount < maxRetries) {
        const backoff = Math.pow(2, retryCount) * 200; // Exponential backoff
        setTimeout(() => {
          dispatch("syncCompany", retryCount + 1);
        }, backoff);
      } else {
        console.error(
          "Failed to sync company after",
          maxRetries,
          "attempts:",
          error
        );
        commit("setCompany", null);
      }
    });
}

export function syncLicenses({ commit, dispatch, rootState }, retryCount = 0) {
  const maxRetries = 3;

  $API.CURRENT_COMPANY_LICENSES.GET()
    .then(({ data }) => {
      commit("setLicenses", data.licenses);
      //get current license from a "current" module.
      let currentLicense = rootState.current.license;
      const defaultLicense = getPreferredDefaultLicense(data.licenses);
      if (!currentLicense) {
        // Prefer central distribution when choosing the initial default.
        commit("current/setLicense", defaultLicense, { root: true });
      } else {
        //find current license in all licenses list.
        let currentLicenseFromList = data.licenses.find(
          (l) => currentLicense.id === l.id
        );
        //check that current license is available.
        if (!currentLicenseFromList) {
          //clear listing chat.
          commit("listingV2/clearFormData", null, { root: true });
          // Prefer central distribution when resetting the default.
          commit("current/setLicense", defaultLicense, { root: true });
          //clear inventory reorder state.
          commit("inventoryReorder/clearState", null, { root: true });
          //clear reorder inventory.
          clearInventoryReorderCache();
        } else {
          //update current license in state so that we always have an updated object.
          commit("current/setLicense", currentLicenseFromList, { root: true });
        }
      }
    })
    .catch((error) => {
      // Don't retry on 404
      if (error?.response?.status === 404) {
        console.warn("User has no licenses");
        commit("setLicenses", []);
        return;
      }

      if (retryCount < maxRetries) {
        const backoff = Math.pow(2, retryCount) * 200;
        setTimeout(() => {
          dispatch("syncLicenses", retryCount + 1);
        }, backoff);
      } else {
        console.error(
          "Failed to sync licenses after",
          maxRetries,
          "attempts:",
          error
        );
        commit("setLicenses", []);
      }
    });
}

export function syncPaymentMethods({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.CURRENT_COMPANY_PAYMENT_METHODS.GET()
    .then(({ data }) => {
      commit("setPaymentMethods", data.payment_methods);
    })
    .catch((error) => {
      if (error?.response?.status === 404) {
        commit("setPaymentMethods", []);
        return;
      }

      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncPaymentMethods", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync payment methods");
        commit("setPaymentMethods", []);
      }
    });
}

export function syncCompanyContacts(
  { commit, dispatch },
  { poll = false } = {}
) {
  if (contactsInFlight) return Promise.resolve(); // de-dupe
  contactsInFlight = true;

  return $API.CURRENT_COMPANY_CONTACTS.GET()
    .then(({ data }) => {
      if (data.contacts) {
        commit("setCompanyContacts", data.contacts);
      }
      if (data?.contact_people) {
        commit("setCompanyContacts", data.contact_people);
      }
    })
    .catch((err) => {
      if (isRetryable(err) && contactsAttempts < 4) {
        // bounded retries + backoff
        const backoff = Math.min(30_000, 500 * 2 ** contactsAttempts++);
        clearTimeout(contactsTimer);
        contactsTimer = setTimeout(() => {
          dispatch("syncCompanyContacts", { poll });
        }, backoff);
      } else {
        clearTimeout(contactsTimer); // stop on persistent 500s
        contactsAttempts = 0;
      }
    })
    .finally(() => {
      contactsInFlight = false;
    });
}

export function syncOrderStatues({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.ORDERS.GET(`statuses`)
    .then(({ data }) => {
      commit("setOrderStatuses", data.order_statuses);
    })
    .catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncOrderStatues", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync order statuses");
      }
    });
}

export function syncOrderTypes({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.ORDERS.GET(`types`)
    .then(({ data }) => {
      commit("setOrderTypes", data.order_types);
    })
    .catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncOrderTypes", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync order types");
      }
    });
}

export function syncOrderSources({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.ORDERS.GET(`sources`)
    .then(({ data }) => {
      commit("setOrderSources", data.order_sources);
    })
    .catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncOrderSources", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync order sources");
      }
    });
}

export function syncPaymentTerms({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.PAYMENT_TERMS.GET()
    .then(({ data }) => {
      commit("setPaymentTerms", data.payment_terms);
    })
    .catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncPaymentTerms", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync payment terms");
      }
    });
}

export function syncPaymentStatuses({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.PAYMENT_STATUSES.GET()
    .then(({ data }) => {
      commit("setPaymentStatuses", data.payment_statuses);
    })
    .catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncPaymentStatuses", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync payment statuses");
      }
    });
}

export function syncGrowMedia({ commit, dispatch }, retryCount = 0) {
  $API.GROW_MEDIA.GET()
    .then(({ data }) => {
      commit("setGrowMedia", data.grow_media);
    })
    .catch((error) => {
      if (retryCount < 5) {
        let timeout = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          dispatch("syncGrowMedia", retryCount + 1);
        }, timeout);
      } else {
        console.error(
          "Failed to sync grow media after several attempts:",
          error
        );
      }
    });
}

export function syncCompanyShippingBoxes({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.CURRENT_COMPANY_BOXES.GET()
    .then(({ data }) => {
      commit("setCompanyShippingBoxes", data.company_shipping_boxes);
    })
    .catch((error) => {
      if (error?.response?.status === 404) {
        commit("setCompanyShippingBoxes", []);
        return;
      }

      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncCompanyShippingBoxes", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync shipping boxes");
        commit("setCompanyShippingBoxes", []);
      }
    });
}

export function addTempShippingBox({ commit, state }, box) {
  if (box && Object.keys(box).length > 0) {
    let currentKey = state.temp_shipping_boxes.length;
    box.id = `temp-${currentKey}`;
    commit("addTempShippingBox", box);
    return box;
  }
}

export function editTempShippingBox({ commit, state }, box) {
  if (box && Object.keys(box).length > 0 && box.id) {
    commit("editTempShippingBox", box);
    return box;
  }
}

export function updateInventoryCategoryTypesOG({ commit }, items) {
  return new Promise((resolve, reject) => {
    const uniqueCategoryTypes = items.reduce((acc, item) => {
      if (
        item.category_type &&
        !acc.find((ct) => ct.id === item.category_type.id)
      ) {
        acc.push(item.category_type);
      }

      if (
        item.product &&
        item.product.category_type &&
        !acc.find((ct) => ct.id === item.product.category_type.id)
      ) {
        acc.push(item.product.category_type);
      }

      return acc;
    }, []);

    commit("setInventoryCategoryTypes", uniqueCategoryTypes);
    resolve(uniqueCategoryTypes);
  });
}

export function updateInventoryCategoryTypes({ commit }, items) {
  return new Promise((resolve, reject) => {
    if (!Array.isArray(items)) {
      console.error("Expected an array, but received:", items);
      reject("Data format error: Expected an array");
      return;
    }

    // Process only if items is an array
    const uniqueCategoryTypes = items.reduce((acc, item) => {
      if (
        item.category_type &&
        !acc.find((ct) => ct.id === item.category_type.id)
      ) {
        acc.push(item.category_type);
      }

      if (
        item.product &&
        item.product.category_type &&
        !acc.find((ct) => ct.id === item.product.category_type.id)
      ) {
        acc.push(item.product.category_type);
      }

      return acc;
    }, []);

    commit("setInventoryCategoryTypes", uniqueCategoryTypes);
    resolve(uniqueCategoryTypes);
  });
}

export function fetchAllData({ commit, dispatch }) {
  console.log("Fetching all data...");
  commit("setLoading", true);

  // Create an abort controller to handle timeouts
  const controller = new AbortController();
  const signal = controller.signal;

  // Set a reasonable timeout (20 seconds)
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, 20000);

  // Use properly configured API requests with abort signal
  return Promise.all([
    $API.AUCTIONS.GET({ signal }),
    $API.INVENTORY_LISTINGS.GET({ signal }),
    $API.PRODUCTS.GET({ signal }),
  ])
    .then(([auctionsResponse, listingsResponse, productsResponse]) => {
      clearTimeout(timeoutId);

      try {
        // Process auctions first as they may be most important
        commit("setAuctions", auctionsResponse.data.auctions);

        // Process listings next
        commit("setListings", listingsResponse.data.inventory_listings);

        // Use a more efficient data processing approach for brands
        // This is similar to your current implementation but with better error handling
        const brands = [];
        const brandMap = new Map(); // More efficient than array.some() in a loop

        // Process auctions for brands
        auctionsResponse.data.auctions.forEach((auction) => {
          if (auction.brand) {
            const brandId = auction.brand.id;
            if (!brandMap.has(brandId)) {
              brandMap.set(brandId, {
                brand: auction.brand,
                auctions: [],
                listings: [],
                products: [],
              });
              brands.push(auction.brand);
            }
            brandMap.get(brandId).auctions.push(auction);
          }
        });

        // Process listings for brands
        listingsResponse.data.inventory_listings.forEach((listing) => {
          if (listing.brand) {
            const brandId = listing.brand.id;
            if (!brandMap.has(brandId)) {
              brandMap.set(brandId, {
                brand: listing.brand,
                auctions: [],
                listings: [],
                products: [],
              });
              brands.push(listing.brand);
            }
            brandMap.get(brandId).listings.push(listing);
          }
        });

        // Process products for brands
        productsResponse.data.products.forEach((product) => {
          if (product.brand) {
            const brandId = product.brand.id;
            if (!brandMap.has(brandId)) {
              brandMap.set(brandId, {
                brand: product.brand,
                auctions: [],
                listings: [],
                products: [],
              });
              brands.push(product.brand);
            }
            brandMap.get(brandId).products.push(product);
          }
        });

        // Commit all brand data in a single batch to reduce reactivity overhead
        commit("setAllBrands", brands);

        // Commit brand-specific data
        brandMap.forEach((data, brandId) => {
          if (data.products.length > 0) {
            commit("setProductsForBrand", {
              brandId,
              products: data.products,
            });
          }

          if (data.auctions.length > 0) {
            commit("setAuctionsForBrand", {
              brandId,
              auctions: data.auctions,
            });
          }

          if (data.listings.length > 0) {
            commit("setListingsForBrand", {
              brandId,
              listings: data.listings,
            });
          }
        });

        // Signal success
        return true;
      } catch (err) {
        console.error("Error processing data:", err);
        commit("setError", "Error processing data: " + err.message);
        // Don't throw error here to allow UI to continue functioning
        return false;
      }
    })
    .catch((error) => {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        console.error("Data fetch timed out");
        commit("setError", "Data loading timed out. Please refresh the page.");
      } else {
        console.error("Failed to fetch data:", error);
        commit("setError", error.message || "Failed to load data");
      }

      // Return false but don't throw to allow UI to continue functioning
      return false;
    })
    .finally(() => {
      commit("setLoading", false);
    });
}

export function fetchStorefronts({ commit }) {
  commit("setLoading", true);
  return $API.BRAND_STOREFRONTS.GET()
    .then(({ data }) => {
      commit("setStorefronts", data.storefronts || []);
      return true;
    })
    .catch((error) => {
      console.error("Failed to fetch storefronts:", error);
      commit("setError", error.message || "Failed to load storefronts");
      return false;
    })
    .finally(() => {
      commit("setLoading", false);
    });
}

export function fetchStorefront({ commit, state }, brandId) {
  commit("setLoading", true);
  return $API.BRAND_STOREFRONTS.GET(brandId)
    .then(({ data }) => {
      commit("setStorefrontData", { brandId, data });
      return data;
    })
    .catch((error) => {
      console.error(`Failed to fetch storefront for brand ${brandId}:`, error);
      commit("setError", error.message || `Failed to load storefront`);
      throw error;
    })
    .finally(() => {
      commit("setLoading", false);
    });
}

export function updateProductBrand(
  { dispatch },
  { productId, brandId, productData }
) {
  return $API.PRODUCTS.POST(`${productId}`, productData)
    .then(() => {
      return dispatch("fetchAllData");
    })
    .then(() => {
      return true;
    })
    .catch((error) => {
      console.error("Failed to update product brand:", error);
      throw error;
    });
}

export function fetchBrandDetails({ commit, state }, brandId) {
  let newData = {
    products: [],
    listings: [],
    auctions: [],
  };

  const promises = [];

  promises.push(
    $API.PRODUCTS.GET().then(async (response) => {
      const brandProducts = response.data.products.filter(
        (product) => product.brand && product.brand.id === brandId
      );
      newData.products = brandProducts;

      const productIds = brandProducts.map((p) => p.id);

      const listingsResponse = await $API.INVENTORY_LISTINGS.GET();
      const brandListings = listingsResponse.data.inventory_listings.filter(
        (listing) =>
          listing.brand_id === brandId ||
          productIds.includes(listing.product_id)
      );
      newData.listings = brandListings;
    })
  );

  promises.push(
    $API.AUCTIONS.GET({ brand: brandId }).then((response) => {
      newData.auctions = response.data.auctions || [];
    })
  );

  return Promise.all(promises).then(() => {
    const existingProducts = state.productsByBrand[brandId] || [];
    const existingListings = state.listingsByBrand[brandId] || [];
    const existingAuctions = state.auctionsByBrand[brandId] || [];

    commit("setProductsForBrand", {
      brandId,
      products: newData.products.length
        ? newData.products
        : state.productsByBrand[brandId] || existingProducts,
    });

    commit("setListingsForBrand", {
      brandId,
      listings: newData.listings.length ? newData.listings : existingListings,
    });

    commit("setAuctionsForBrand", {
      brandId,
      auctions: newData.auctions.length ? newData.auctions : existingAuctions,
    });
  });
}

export function fetchAvailablePackageTags(
  { commit, rootState },
  licenseId = null
) {
  if (!licenseId) {
    licenseId = rootState.current.license?.id;
  }
  if (!licenseId) return Promise.reject("No license selected");

  return $API.AVAILABLE_PACKAGE_TAGS.GET(null, { license: licenseId }).then(
    ({ data }) => {
      commit("setAvailablePackageTags", {
        licenseId,
        tags: data.package_tags,
      });
      return data.package_tags;
    }
  );
}

export function syncProductLines({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.PRODUCT_LINES.GET()
    .then(({ data }) => {
      commit("setProductLines", data.product_lines);
    })
    .catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("syncProductLines", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync product lines");
      }
    });
}

export function syncMetrcKeyStatus({ commit, dispatch }, options = {}) {
  // TEMPORARILY DISABLED: METRC key status blocking has a logic flaw.
  // Bypassing fetch so no licenses are flagged as having bad keys.
  // TODO: Re-enable once root cause is identified and fixed.
  return;

  const { force = false, retryCount = 0 } = typeof options === 'object' ? options : { retryCount: options };
  const maxRetries = 3;

  $API.METRC_KEY_STATUS.GET(null, force ? { force: true } : undefined)
    .then(({ data }) => {
      commit("setMetrcKeyStatus", data);
    })
    .catch((error) => {
      if (error?.response?.status === 404) {
        return;
      }

      if (retryCount < maxRetries) {
        const backoff = Math.pow(2, retryCount) * 200;
        setTimeout(() => {
          dispatch("syncMetrcKeyStatus", { force, retryCount: retryCount + 1 });
        }, backoff);
      } else {
        console.error("Failed to sync metrc key status");
      }
    });
}

export function applyStoredSectionAssignments({ commit, state }, brandId) {
  const key = `brand_${brandId}_sections`;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return;

    const assignments = JSON.parse(stored);

    Object.entries(assignments).forEach(([productId, sectionId]) => {
      const brand = state.brands[brandId];
      if (!brand || !brand.product_sections) return;

      const section = brand.product_sections.find(
        (s) => s.id === Number(sectionId)
      );
      if (section) {
        commit("updateProductSection", {
          productId: Number(productId),
          section,
        });
      }
    });
  } catch (e) {
    console.error("Error applying stored section assignments:", e);
  }
}

export function syncAppNotifications({ commit, dispatch }, retryCount = 0) {
  const maxRetries = 3;

  $API.CURRENT_USER.GET('app-notifications').then(({ data }) => {
      commit("setAppNotifications", data.app_notifications);
    }).catch((error) => {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          dispatch("setAppNotifications", retryCount + 1);
        }, Math.pow(2, retryCount) * 200);
      } else {
        console.error("Failed to sync app notifications");
      }
    });
}
